import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  PageHeader,
  DetailCard,
  BulkImportLineItemRow,
  LineItemsTable,
  LineItemSearchBar,
  BarcodeScannerModal,
  MissingBarcodeDialog,
} from '../components';
import { getMenuPricesByInventoryUuids } from '../api/products';
import {
  describeLine,
  findLinesMissingBarcode,
  generateUniqueBarcodes,
} from '../utils/barcode';
import {
  findSkuConflicts,
  nextLineItemId,
  snapshotBeforeLink,
  unlinkPatch,
  withoutSearchBarcode,
} from '../utils/lineItems';
import { useBulkImportItems } from '../hooks/useBulkImport';
import { useCostPreview } from '../hooks/useCostPreview';
import { useCategories } from '../hooks/useCategories';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, formatCurrency, IMPORT_ACTION_CHIP } from '../constants';

const emptyItem = () => ({
  // Stable identity, used as the React key and to address the line in state.
  id: nextLineItemId(),
  search: '',
  sku: '',
  name: '',
  barcodes: [],
  productType: 'product',
  quantity: '',
  unitCost: '',
  // MMS menu/selling price. Distinct from unitCost (purchase cost).
  sellingPrice: '',
  unit: 'each',
  expiryDate: '',
  categoryId: null,
  categoryName: null,
  selectedItem: null,
  // Field values from before this line was linked to an existing item, put back
  // when the link is removed.
  beforeLink: null,
  // Existing item found to already use this line's SKU, offered as a link.
  skuConflict: null,
  // Cost handling for existing items (ignored for new ones).
  costMode: 'update',
  // Per-field validation messages, kept on the item so they stay aligned with
  // their line as items are added/removed.
  errors: {},
});

/**
 * Adds stock for many items in one operation, creating the ones that do not
 * exist yet. Line items are entered exactly as on the manual invoice screen
 * (product search, barcode scan, per-line fields), but nothing is written to
 * purchase invoices: each line applies stock, cost, a lot and a stock
 * transaction directly.
 */
export default function BulkImport() {
  const businessUuid = useBusinessUuid();
  const bulkImport = useBulkImportItems();
  const { data: categories = [] } = useCategories(businessUuid);

  const [items, setItems] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTargetId, setScanTargetId] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [missingBarcodeLines, setMissingBarcodeLines] = useState(null);

  // Resolving a line's category by scanning the list would cost one pass per
  // line on every render of the form.
  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  const totalQuantity = useMemo(
    () => items.reduce((sum, it) => sum + (parseFloat(it.quantity) || 0), 0),
    [items],
  );
  const totalCost = useMemo(
    () =>
      items.reduce(
        (sum, it) =>
          sum + (parseFloat(it.quantity) || 0) * (parseFloat(it.unitCost) || 0),
        0,
      ),
    [items],
  );
  const costPreviews = useCostPreview(items, 'quantity');

  // Line inputs register themselves here by line id and field so a newly added
  // line can be focused without every row having to know its position in the
  // list.
  const lineInputsRef = useRef(new Map());
  const registerLineInput = useCallback((id, field, element) => {
    const key = `${id}:${field}`;
    if (element) lineInputsRef.current.set(key, element);
    else lineInputsRef.current.delete(key);
  }, []);
  const [pendingFocus, setPendingFocus] = useState(null);
  useEffect(() => {
    if (!pendingFocus) return;
    lineInputsRef.current.get(`${pendingFocus.id}:${pendingFocus.field}`)?.focus();
    setPendingFocus(null);
  }, [pendingFocus]);

  // `updates` may be an object or, when the patch depends on the line's current
  // values, a function of that line. The function form lets the rows keep stable
  // handlers, which is what keeps them from re-rendering on unrelated edits.
  const updateItem = useCallback(
    (id, updates) =>
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, ...(typeof updates === 'function' ? updates(it) : updates) }
            : it,
        ),
      ),
    [],
  );
  // New lines go to the top of the table, under the "Add item" action;
  // focusing the new line's search box lets the user type/scan straight away.
  const addItem = useCallback(() => {
    const item = emptyItem();
    setItems((prev) => [item, ...prev]);
    setPendingFocus({ id: item.id, field: 'search' });
  }, []);
  // A code that matched no barcode starts a new product: the line gets the
  // code as its barcode, and focus goes to Name, the next field the user has
  // to fill in.
  const addNewItemWithBarcode = useCallback((code) => {
    const item = { ...emptyItem(), barcodes: [code] };
    setItems((prev) => [item, ...prev]);
    setPendingFocus({ id: item.id, field: 'name' });
  }, []);
  const removeItem = useCallback(
    (id) => setItems((prev) => prev.filter((it) => it.id !== id)),
    [],
  );

  // Prefill a matched line's Price with the product's CURRENT menu selling price
  // from MMS. Best-effort: only fills lines that are still blank, and never
  // blocks the form if the lookup fails.
  const prefillSellingPrices = useCallback(async (uuids) => {
    const list = (uuids || []).filter(Boolean);
    if (!list.length) return;
    try {
      const prices = await getMenuPricesByInventoryUuids(list);
      setItems((prev) =>
        prev.map((it) => {
          const uuid = it.selectedItem?.uuid;
          if (
            uuid &&
            prices[uuid] != null &&
            (it.sellingPrice === '' || it.sellingPrice == null)
          ) {
            return { ...it, sellingPrice: String(prices[uuid]) };
          }
          return it;
        }),
      );
    } catch (e) {
      console.error('Failed to load current menu price(s)', e);
    }
  }, []);

  const handleItemSelected = useCallback(
    (id, item) => {
      const updates = {
        search: item.sku,
        sku: item.sku,
        name: item.name,
        unit: item.unit,
        barcodes: item.barcodes || [],
        // Existing item: only the category id comes from inventory; the display
        // name is resolved from the categories list. Read-only for existing items.
        categoryId: item.categoryId ?? null,
        categoryName: null,
        selectedItem: item,
        productType: item.type,
        // Changing the selected product must reset the menu price so it is
        // refilled below with THIS product's current MMS price.
        sellingPrice: '',
      };
      if (item.unitPrice != null && Number.isFinite(Number(item.unitPrice))) {
        updates.unitCost = String(item.unitPrice);
      }
      setItems((prev) =>
        prev.map((line) =>
          line.id === id
            ? {
                ...line,
                ...updates,
                // Keep the first snapshot: picking a different item afterwards
                // must still undo back to what the user had typed.
                beforeLink:
                  line.beforeLink ?? snapshotBeforeLink(withoutSearchBarcode(line)),
                skuConflict: null,
                errors: { ...(line.errors || {}), sku: undefined },
              }
            : line,
        ),
      );
      if (item.uuid) prefillSellingPrices([item.uuid]);
    },
    [prefillSellingPrices],
  );

  const handleUnlink = useCallback(
    (id) => updateItem(id, (line) => unlinkPatch(line, emptyItem())),
    [updateItem],
  );

  // Adds an existing item as a new line, linked exactly as if it had been
  // picked in that line's own search box.
  const prependLinkedItem = useCallback(
    (item) => {
      const line = emptyItem();
      setItems((prev) => [line, ...prev]);
      handleItemSelected(line.id, item);
    },
    [handleItemSelected],
  );

  const handleBarcodeScanRequest = useCallback((id) => {
    setScanTargetId(id);
    setScannerOpen(true);
  }, []);

  // The search bar's scan button targets no line, so a found item is added as a new line.
  const handleSearchBarScan = useCallback(() => {
    setScanTargetId(null);
    setScannerOpen(true);
  }, []);

  const handleBarcodeFound = (item) => {
    if (scanTargetId && items.some((it) => it.id === scanTargetId)) {
      handleItemSelected(scanTargetId, item);
    } else {
      prependLinkedItem(item);
    }
  };

  const handleSubmit = async () => {
    setError(null);
    if (items.length === 0) {
      setError('Add at least one item.');
      return;
    }

    // Validate every line and attach a message to each individual field, so it
    // renders directly under that field instead of one generic message that
    // stops at the first bad line.
    let hasFieldErrors = false;
    const validated = items.map((it) => {
      const errors = {};
      if (!it.sku.trim() && !it.name.trim()) {
        errors.identity = 'Name or SKU is required.';
      }
      const qty = parseFloat(it.quantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        errors.quantity = 'Quantity must be greater than zero.';
      }
      const cost = parseFloat(it.unitCost);
      if (!Number.isFinite(cost) || cost <= 0) {
        errors.unitCost = 'Unit cost must be greater than zero.';
      }
      if (it.sellingPrice === '' || it.sellingPrice == null) {
        errors.sellingPrice = 'Price is required.';
      } else {
        const selling = parseFloat(it.sellingPrice);
        if (!Number.isFinite(selling) || selling < 0) {
          errors.sellingPrice = 'Price must be zero or greater.';
        }
      }
      const productType = it.selectedItem?.type || it.productType;
      if (!it.selectedItem && productType === 'product' && !it.categoryId) {
        errors.category = 'Menu category is required for new products.';
      }
      if (Object.keys(errors).length) hasFieldErrors = true;
      return { ...it, errors };
    });

    if (hasFieldErrors) {
      setItems(validated);
      return;
    }

    // A line with nothing linked creates a new item, and a SKU is unique per
    // business, so a SKU that is already taken is caught here: the user links
    // the line to that item or gives it a different SKU, instead of getting a
    // failed row back from the import.
    let skuConflicts;
    try {
      skuConflicts = await findSkuConflicts(validated, businessUuid);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          'Could not check the SKUs against existing items. Nothing was imported.',
      );
      return;
    }
    if (skuConflicts.size > 0) {
      setItems(
        validated.map((it, idx) => {
          const match = skuConflicts.get(idx);
          if (!match) return it;
          return {
            ...it,
            skuConflict: match,
            errors: {
              ...it.errors,
              sku: `SKU "${it.sku}" already belongs to "${match.name}".`,
            },
          };
        }),
      );
      return;
    }
    // Nothing left to flag: drop the messages from an earlier attempt.
    setItems(validated);

    // New items with no barcode cannot be scanned later, so ask what to do
    // before creating them.
    const missing = findLinesMissingBarcode(validated);
    if (missing.length > 0) {
      setMissingBarcodeLines(missing);
      return;
    }

    await submitItems(validated);
  };

  const submitItems = async (lineItems) => {
    try {
      const response = await bulkImport.mutateAsync({
        businessUuid,
        items: lineItems.map((it) => ({
          // Sent only for a line linked to an existing item. Without it the
          // backend creates a new item instead of matching one.
          productUuid: it.selectedItem?.uuid || undefined,
          sku: it.sku || undefined,
          name: it.name || it.sku,
          barcodes: it.barcodes?.length ? it.barcodes : undefined,
          productType: it.selectedItem?.type || it.productType,
          quantity: parseFloat(it.quantity) || 0,
          unitCost: parseFloat(it.unitCost) || 0,
          sellingPrice:
            it.sellingPrice !== '' &&
            it.sellingPrice != null &&
            Number.isFinite(parseFloat(it.sellingPrice))
              ? parseFloat(it.sellingPrice)
              : undefined,
          unit: it.unit || undefined,
          expiryDate: it.expiryDate || undefined,
          categoryId: it.selectedItem ? undefined : it.categoryId || undefined,
          categoryName: it.selectedItem
            ? undefined
            : it.categoryName || undefined,
          // Cost handling only applies to existing (linked) items.
          costMode: it.selectedItem ? it.costMode || 'update' : undefined,
        })),
      });
      setResult(response);
      setItems([]);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
  };

  const handleGenerateMissingBarcodes = async () => {
    const indexes = missingBarcodeLines || [];
    try {
      const codes = await generateUniqueBarcodes(indexes.length, businessUuid);
      const next = items.map((it, idx) => {
        const position = indexes.indexOf(idx);
        return position === -1 ? it : { ...it, barcodes: [codes[position]] };
      });
      setItems(next);
      setMissingBarcodeLines(null);
      await submitItems(next);
    } catch (e) {
      setMissingBarcodeLines(null);
      setError(
        e?.response?.data?.message ||
          'Failed to generate barcodes. Nothing was imported.',
      );
    }
  };

  return (
    <>
      <Backdrop
        open={bulkImport.isPending}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Importing {items.length} items...
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            This can take a few moments. Please don&apos;t close this page.
          </Typography>
        </Stack>
      </Backdrop>

      <PageHeader
        title="Bulk Import"
        subtitle="Add stock for many items at once. Items that don't exist yet are created."
        backTo="/inventory-management/import"
      />

      {error && (
        <Typography variant="body2" sx={{ color: colors.error, mb: 2 }}>
          {error}
        </Typography>
      )}

      <Stack spacing={2.5}>
        <DetailCard title={`Items ${items.length ? `(${items.length})` : ''}`}>
          <LineItemSearchBar
            onItemSelected={prependLinkedItem}
            onCreateNew={addNewItemWithBarcode}
            onBarcodeScan={handleSearchBarScan}
          />
          <Box sx={{ mt: 1.5 }}>
            <LineItemsTable
              itemCount={items.length}
              totalQuantity={totalQuantity}
              totalCost={totalCost}
              onAddItem={addItem}
              emptyMessage={'No items added yet. Use "Add item" to start.'}
            >
              {items.map((item) => {
                const preview = costPreviews.get(item.id);
                return (
                  <BulkImportLineItemRow
                    key={item.id}
                    item={item}
                    categories={categories}
                    categoryById={categoryById}
                    onUpdate={updateItem}
                    onRemove={removeItem}
                    onItemSelected={handleItemSelected}
                    onUnlink={handleUnlink}
                    onBarcodeScan={handleBarcodeScanRequest}
                    registerLineInput={registerLineInput}
                    previewStatus={preview?.status}
                    previewNewCost={preview?.newCost}
                    previewError={preview?.error}
                  />
                );
              })}
            </LineItemsTable>
          </Box>
        </DetailCard>

        <DetailCard>
          <Grid container spacing={2.5} justifyContent="flex-end">
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.navy, mb: 1 }}>
                Import Details
              </Typography>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: colors.gray600 }}>
                    Items
                  </Typography>
                  <Typography variant="body2">{items.length}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: colors.gray600 }}>
                    Total quantity
                  </Typography>
                  <Typography variant="body2">{totalQuantity}</Typography>
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ pt: 1, borderTop: `1px solid ${colors.border}` }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Total cost
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: colors.primary }}
                  >
                    {formatCurrency(totalCost)}
                  </Typography>
                </Stack>
                <Box sx={{ pt: 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={bulkImport.isPending || items.length === 0}
                    sx={{
                      backgroundColor: colors.primary,
                      textTransform: 'none',
                      fontWeight: 600,
                      py: 1.25,
                    }}
                  >
                    Import {items.length || ''} Items
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DetailCard>

          {result && (
            <DetailCard title="Import Result">
              <Stack
                direction="row"
                spacing={1}
                sx={{ mb: 2, flexWrap: 'wrap' }}
                alignItems="center"
              >
                <Chip size="small" color="success" label={`${result.createdCount} created`} />
                <Chip size="small" color="info" label={`${result.updatedCount} updated`} />
                {result.failedCount > 0 && (
                  <Chip size="small" color="error" label={`${result.failedCount} failed`} />
                )}
              </Stack>
              <Box
                sx={{
                  maxHeight: 380,
                  overflow: 'auto',
                  border: `1px solid ${colors.border}`,
                  borderRadius: 1,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Item</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell>Result</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Stock</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.rows.map((r) => (
                      <TableRow key={r.row}>
                        <TableCell>{r.row + 1}</TableCell>
                        <TableCell>
                          {r.name}
                          {r.error && (
                            <Typography
                              variant="caption"
                              sx={{ color: colors.error, display: 'block' }}
                            >
                              {r.error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ color: colors.primary }}>{r.sku || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={IMPORT_ACTION_CHIP[r.action]?.color || 'default'}
                            label={IMPORT_ACTION_CHIP[r.action]?.label || r.action}
                          />
                        </TableCell>
                        <TableCell align="right">{r.quantity}</TableCell>
                        <TableCell align="right">
                          {r.previousStock == null
                            ? '-'
                            : `${r.previousStock} \u2192 ${r.newStock}`}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                <Button onClick={() => setResult(null)} sx={{ textTransform: 'none' }}>
                  Dismiss
                </Button>
              </Stack>
            </DetailCard>
          )}
      </Stack>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleBarcodeFound}
        onBarcodeNotFound={(code) => {
          if (scanTargetId && items.some((it) => it.id === scanTargetId)) {
            setItems((prev) =>
              prev.map((it) =>
                it.id === scanTargetId
                  ? {
                      ...it,
                      search: code,
                      selectedItem: null,
                      barcodes: it.barcodes.includes(code)
                        ? it.barcodes
                        : [...it.barcodes, code],
                    }
                  : it,
              ),
            );
          } else {
            setItems((prev) => [
              ...prev,
              { ...emptyItem(), search: code, barcodes: [code] },
            ]);
          }
          setError(`No product found for barcode "${code}". Added as a new barcode.`);
        }}
      />

      <MissingBarcodeDialog
        open={Boolean(missingBarcodeLines?.length)}
        lines={(missingBarcodeLines || []).map((idx) =>
          describeLine(items[idx] || {}, idx),
        )}
        loading={bulkImport.isPending}
        onCancel={() => setMissingBarcodeLines(null)}
        onAutogenerate={handleGenerateMissingBarcodes}
        onContinue={() => {
          setMissingBarcodeLines(null);
          submitItems(items);
        }}
      />
    </>
  );
}
